import React, {Component} from 'react'
import {graphql, withApollo} from 'react-apollo'
import {
  find,
  get,
  reject,
  isEmpty,
  countBy,
  size,
  map,
  sumBy,
  filter,
  minBy,
  flowRight as compose,
  isNil,
  first,
  isEqual,
} from 'lodash'
import {withNamespaces, Trans} from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import {
  currencies,
  withdrawalPaymentVendors,
  kycStatuses,
  withdrawalStatuses,
} from '@bdswiss/common-enums'
import {Loading} from '../../Common/Loading'
import {withRouter, Link} from 'react-router-dom'
import ChooseMethod from './ChooseMethod'
import ChooseAccount from './ChooseAccount'
import ChooseAvailableOption from './ChooseAvailableOption'
import UiNotification from '../../Common/UiNotification'
import {getItem, getPlatform, isEmptyStr} from '../../../common/utils'
import messages from '../../../assets/messages'
import {
  blockedWithdrawalAccount,
  isCentAccount,
  isPammInvestorAccount,
  isPammManagerAccount,
  isPammWithdrawalBlockedOnWeekend,
} from '../../../common/utils/accounts'
import {getFormattedAmount, getMissingDocs} from '../../../common/utils/general'
import {CREATE_WITHDRAWAL_MUTATION} from '../../../graphql/mutations'
import {
  ACCOUNTS_QUERY,
  CLIENT_DATA_QUERY,
  PAYMENTS_ACCOUNTS_QUERY,
  PROFILE_SETTINGS_QUERY,
  WITHDRAWABLE_METHODS_QUERY,
} from '../../../graphql/queries'
import NotificationBar from '../../Common/NotificationBar'
import AppContext from '../../Common/contexts/AppContext'
import Images from '../../Common/Images'
import CustomNotificationAlert from '../../Common/CustomNotificationAlert'
import PageSubTitle from '../../Common/PageSubTitle'
import classnames from 'classnames'
import {config} from '../../../config'
import {InnerAppContext} from '../../../common/types'
import {Account} from '../../../graphql/types'
import {WithdrawalBlockedPopup} from '../../WithdrawalBlockedPopup'
import {WithdrawalSummary} from './WithdrawalSummary'
import {WarningWithdrawalSection} from './WarningWithdrawalSection'
import {PammInvestorAccountMessage} from './PammInvestorAccountMessage'
import {styles} from './Withdraw.styles'

const pendingWithdrawalStatuses = [
  withdrawalStatuses.pending.key,
  withdrawalStatuses.notProcessed.key,
]

class Withdraw extends Component<any, any> {
  static contextType = AppContext
  context!: InnerAppContext
  constructor(props) {
    super(props)
    this.state = {
      form: {
        account: '',
        amount: '',
        method: '',
        availableMethod: {},
        fields: {},
      },
      accountSelected: false,
      refundSelected: false,
      withdrawalBlockedPopupOpen: false,
      skipRefund: false,
      uiNotification: {
        show: false,
        status: '',
      },
      submitLoading: false,
      methodsLoading: false,
      currentAccount: {},
      lydianWithdrawalModal: {
        show: false,
        status: '',
        message: '',
      },
      pammWithdrawalModalNotificationSuccess: {show: false},
    }
  }

  componentDidMount() {
    this.checkAndShowWithdrawalPopup()
  }

  componentDidUpdate(prevProps, prevState) {
    const prevAccountId = prevState.currentAccount?.id
    const currentAccountId = this.state.currentAccount?.id

    if (prevAccountId !== currentAccountId) {
      this.checkAndShowWithdrawalPopup()
    }
  }

  checkAndShowWithdrawalPopup() {
    const {currentAccount, withdrawalBlockedPopupOpen} = this.state

    const disabledWithdrawal =
      currentAccount && currentAccount.credit > 0 && currentAccount.hasOpenPositions

    if (disabledWithdrawal && !withdrawalBlockedPopupOpen) {
      this.setState({withdrawalBlockedPopupOpen: true})
    }
  }

  static getDerivedStateFromProps(props, state) {
    const {accounts, match} = props
    const {currentAccount} = state
    const {params: urlParams} = match

    // if account is not selected, pick from url params or first from list
    if (accounts.length > 0 && isEqual(currentAccount, {})) {
      const accountIdFromParams = Number(get(urlParams, 'accountId'))
      const selectedAccount = accountIdFromParams
        ? accounts.find((account) => account.id === accountIdFromParams)
        : accounts[0]

      if (selectedAccount == null) return null

      return {
        currentAccount: selectedAccount,
        form: {
          account: selectedAccount,
          accountId: selectedAccount.id,
        },
      }
    }
    return null
  }

  accountSelected(accountId, amount) {
    const {accounts, client: apolloClient} = this.props

    this.setState({methodsLoading: true})
    apolloClient
      .query({
        query: WITHDRAWABLE_METHODS_QUERY,
        variables: {id: accountId},
        fetchPolicy: 'no-cache',
      })
      .then((res) => {
        const withdrawablePaymentMethods =
          get(res.data, 'viewer.accounts[0].withdrawablePaymentMethods') || []
        const firstVendor = minBy(withdrawablePaymentMethods, 'order')
        const refundSelected = Number(amount) <= get(firstVendor, 'amountLeft', Infinity)
        const selectedAccount = find(accounts, {id: accountId})

        this.setState((state) => ({
          currentAccount: selectedAccount,
          form: {
            ...state.form,
            account: {
              ...selectedAccount,
              withdrawablePaymentMethods,
            },
            amount: Number(amount),
            availableMethod: refundSelected ? firstVendor : null,
          },
          accountSelected: true,
          methodsLoading: false,
          refundSelected,
        }))
      })
      .catch(() => {
        this.setState({methodsLoading: false})
      })
  }

  refundSelected(availableMethod) {
    this.setState((state) => ({
      form: {
        ...state.form,
        availableMethod,
      },
      refundSelected: true,
    }))
  }

  goToAccountsPage = () => {
    this.props.history.push('/accounts')
  }

  goBack = () => {
    this.setState({accountSelected: false, refundSelected: false, skipRefund: false})
  }

  // TODO: Remove as unused?
  createWithdrawalRequest(variables) {
    const {createWithdrawalRequest} = this.props
    this.setState({submitLoading: true})

    createWithdrawalRequest({variables})
      .then(() => {
        this.setState((state) => ({
          uiNotification: {
            show: true,
            status: 'success',
          },
          form: {
            ...state.form,
            method: variables.paymentVendor,
          },
          submitLoading: false,
          accountSelected: false,
        }))
      })
      .catch((e) => {
        this.setState({
          uiNotification: {
            show: true,
            status: 'failure',
            errorText: e.message.replace('GraphQL error: ', ''),
          },
          submitLoading: false,
        })
      })
  }

  handleChange(prop, value) {
    this.setState((state) => ({
      form: {
        ...state.form,
        [prop]: value,
      },
    }))
  }

  checkRemainingRefunds(amountToWithdraw, availableMethod) {
    const {
      refundSelected,
      form: {amount, account},
      withdrawablePaymentMethods,
    } = this.state

    if (refundSelected && amountToWithdraw < amount) {
      const currentMethods = !isEmpty(withdrawablePaymentMethods)
        ? withdrawablePaymentMethods
        : get(account, 'withdrawablePaymentMethods')
      const updatedMethods = reject(
        map(currentMethods, (m) => {
          if (m.id === availableMethod.id) {
            m.amountLeft = Number((m.amountLeft - amountToWithdraw).toFixed(2))
          }
          return m
        }),
        (m) => m.amountLeft <= 0,
      )
      this.setState((state) => ({
        uiNotification: {show: false, status: ''},
        refundSelected: false,
        withdrawablePaymentMethods: updatedMethods,
        form: {...state.form, amount: amount - amountToWithdraw},
        availableMethod: {},
        accountSelected: true,
      }))
    } else {
      this.setState({withdrawablePaymentMethods: []})
      this.props.history.push('/')
    }
  }

  copyTradingWithdrawalMsg(copyTradingAccount: Account) {
    const {classes} = this.props

    const {copyTrading, activeBrokereeCopyTradingOffer} = copyTradingAccount

    if (copyTrading != null) {
      const accountLabel = copyTrading.accountCopying?.accountName ?? ''
      const isActiveCopying = copyTrading.isActive

      return (
        !isNil(copyTradingAccount) && (
          <NotificationBar status="error">
            <Trans
              {...messages[
                isActiveCopying ? 'copyTradingWithdrawal' : 'copyTradingWithdrawalPending'
              ]}
              values={{
                ibAccount: `${accountLabel} - ${copyTrading.accountCopying?.remoteId ?? ''}`,
              }}
              components={[
                <span className={classes.highlight}>account</span>,
                <Link
                  to={`/accounts/${copyTradingAccount.id}/copyTrading`}
                  className={classes.link}
                >
                  {' '}
                </Link>,
              ]}
            />
          </NotificationBar>
        )
      )
    }

    if (activeBrokereeCopyTradingOffer != null) {
      const {name, isUnsubscribePending} = activeBrokereeCopyTradingOffer

      return (
        <NotificationBar status="error">
          <Trans
            {...messages[
              isUnsubscribePending ? 'copyTradingWithdrawalPending' : 'copyTradingWithdrawal'
            ]}
            values={{ibAccount: name}}
            components={[
              <span className={classes.highlight}>account</span>,
              <Link to={`/accounts/${copyTradingAccount.id}/copyTrading`} className={classes.link}>
                {' '}
              </Link>,
            ]}
          />
        </NotificationBar>
      )
    }

    return (
      <NotificationBar status="error">
        <Trans {...messages.somethingWrongError} />
      </NotificationBar>
    )
  }

  getAccountId(accountSelected) {
    this.setState({
      currentAccount: accountSelected,
    })
  }

  setLydianModalData(dataModal) {
    this.setState({
      lydianWithdrawalModal: dataModal,
    })
  }

  createLydianWithdrawalRequest(variables, t) {
    const {createWithdrawalRequest} = this.props
    this.setState({submitLoading: true})

    createWithdrawalRequest({variables})
      .then(() => {
        this.setState({
          lydianWithdrawalModal: {
            show: true,
            status: 'success',
            message: t(
              messages.lydianSuccessWithdrawal.i18nKey,
              messages.lydianSuccessWithdrawal.defaults,
            ),
          },
          submitLoading: false,
          accountSelected: false,
        })
      })
      .catch((e) => {
        this.setState({
          lydianWithdrawalModal: {
            show: true,
            status: 'failure',
            message: e.message.replace('GraphQL error: ', ''),
          },
          submitLoading: false,
        })
      })
  }

  createPammWithdrawalRequestAction = async (accountId, amount) => {
    const {createWithdrawalRequest, t, accounts} = this.props
    const account = find(accounts, {id: accountId})

    if (account && isPammWithdrawalBlockedOnWeekend(account)) {
      this.setState({
        uiNotification: {
          show: true,
          status: 'failure',
          errorText: t(
            messages.pammWithdrawalWeekendNotAllowed.i18nKey,
            messages.pammWithdrawalWeekendNotAllowed.defaults,
          ),
        },
      })
      return
    }

    try {
      this.setState({submitLoading: true})

      // Ensure amount is a number, not a string - GraphQL 16 requires strict types
      // Convert to primitive number explicitly to avoid any string serialization issues
      const numericAmount = +amount // Unary plus operator ensures primitive number
      const numericAccountId = +accountId // Unary plus operator ensures primitive number

      // Validate that conversion was successful
      if (isNaN(numericAmount) || isNaN(numericAccountId) || numericAmount <= 0) {
        throw new Error('Invalid amount or account ID')
      }

      await createWithdrawalRequest({
        variables: {
          accountId: numericAccountId,
          amount: numericAmount,
          paymentVendor: withdrawalPaymentVendors.pammWithdrawal.value,
          paymentFields: {
            termsAndConditions: 'accept',
          },
          platform: JSON.stringify(getPlatform()),
        },
      })

      this.setState({
        pammWithdrawalModalNotificationSuccess: {show: true},
        submitLoading: false,
        accountSelected: false,
      })
    } catch (e) {
      const errorText =
        e instanceof Error ? e.message.replace('GraphQL error: ', '') : 'Something went wrong'

      this.setState({
        uiNotification: {show: true, status: 'failure', errorText},
        submitLoading: false,
      })
    }
  }

  handleSelectAccountSubmit = async (accountId, amount) => {
    const {accounts, t} = this.props
    const account = find(accounts, {id: accountId})
    const isPammAccount =
      account && (isPammInvestorAccount(account) || isPammManagerAccount(account))

    if (isPammAccount && isPammWithdrawalBlockedOnWeekend(account)) {
      this.setState({
        uiNotification: {
          show: true,
          status: 'failure',
          errorText: t(
            messages.pammWithdrawalWeekendNotAllowed.i18nKey,
            messages.pammWithdrawalWeekendNotAllowed.defaults,
          ),
        },
      })
      return
    }

    if (isPammAccount) {
      await this.createPammWithdrawalRequestAction(accountId, amount)

      return
    }

    this.accountSelected(accountId, amount)
  }

  get accountsWithBalance() {
    const {accounts} = this.props

    return reject(
      accounts,
      (account) =>
        account.isDemo ||
        account.isHidden ||
        account.withdrawalsBlocked ||
        (get(account, 'balance') || 0) <= 0 ||
        blockedWithdrawalAccount(account),
    )
  }

  // PT-5331: PAMM Manager and PAMM Investor withdrawals are not direct payouts —
  // funds move to a linked account, so they are exempt from the KYC requirement.
  // Standard accounts are still gated on `kyc_status === approved`.
  get withdrawableAccountsForKyc() {
    const accounts = this.accountsWithBalance
    if (this.props.viewer.kycStatus === kycStatuses.approved.key) return accounts
    return accounts.filter(
      (account) => isPammManagerAccount(account) || isPammInvestorAccount(account),
    )
  }

  get selectedAccount() {
    const firstAccount = this.withdrawableAccountsForKyc[0]
    const {currentAccount} = this.state
    const account = isEqual(currentAccount, {}) ? firstAccount : currentAccount

    return account
  }

  get isShowWarningMessage() {
    const account = this.selectedAccount
    if (!account) return false

    return account.hasOpenPositions || account.credit > 0
  }

  get isPammInvestorAccountSelected() {
    const account = this.selectedAccount
    if (!account) return false

    return isPammInvestorAccount(account)
  }

  get isPammManagerAccountSelected() {
    const account = this.selectedAccount
    if (!account) return false

    return isPammManagerAccount(account)
  }

  get isPammWeekendWithdrawalBlocked() {
    return isPammWithdrawalBlockedOnWeekend(this.selectedAccount)
  }

  // TODO: Cleanup flow v1 https://skyground.atlassian.net/browse/PT-3926
  get isWithdrawalFlowV2() {
    return true
  }

  render() {
    const {showTab} = config
    const showDepositTab = showTab?.deposit ?? true
    const {
      viewerLoading,
      accountsLoading,
      classes,
      viewer: {locale, kycStatus, company, email},
      history,
      t,
      viewer,
      documentsLoading,
    } = this.props
    const country = this.props.viewer.address ? this.props.viewer.address.country : null
    const {methodsLoading, lydianWithdrawalModal} = this.state
    const {transactionsConfig, showLydianWithdrawalNotification} = config
    if (viewerLoading || accountsLoading || documentsLoading || methodsLoading) return <Loading />
    const {
      accountSelected,
      form: {account, amount, method, availableMethod},
      uiNotification,
      pammWithdrawalModalNotificationSuccess,
      submitLoading,
      refundSelected,
      skipRefund,
      currentAccount,
    } = this.state

    const methodSubmitMessage = !isEmptyStr(get(uiNotification, 'status'))
      ? [messages[`${get(uiNotification, 'status')}Withdrawal`]]
      : []
    const groupName = get(withdrawalPaymentVendors[method], 'groupName', 'other')
    if (get(uiNotification, 'status') === 'success' && groupName !== 'other') {
      methodSubmitMessage.push(get(messages, `${groupName}WithdrawalSuccess`))
    }

    const {blockedDeposit, themePreference} = this.context
    const missingDocsTotal = getMissingDocs(viewer)
    const missingDocs = get(countBy(missingDocsTotal), 'false') >= size(missingDocsTotal)

    const withdrawablePaymentMethods =
      get(this.state, 'withdrawablePaymentMethods') || get(account, 'withdrawablePaymentMethods')
    const shouldSelectAccount = !accountSelected && !isEmpty(this.withdrawableAccountsForKyc)
    const shouldSelectRefund =
      accountSelected && !isEmpty(withdrawablePaymentMethods) && !refundSelected && !skipRefund
    const shouldSelectMethod =
      (shouldSelectRefund && refundSelected) || (!shouldSelectRefund && accountSelected)
    const amountToWithdraw = Number(
      (!isEmpty(availableMethod) &&
        (amount < get(availableMethod, 'amountLeft')
          ? amount
          : get(availableMethod, 'amountLeft'))) ||
        amount,
    )

    const getCurrentAccount =
      !accountSelected && isEmpty(currentAccount)
        ? first(this.withdrawableAccountsForKyc)
        : currentAccount
    const blockedWithdrawalsCopyTrading = get(getCurrentAccount, 'withdrawalsBlocked')
    const disabledWithdrawal = getCurrentAccount?.credit > 0 && getCurrentAccount?.hasOpenPositions

    const isWithdrawalFlowV1 = !this.isWithdrawalFlowV2
    const hasPendingRequests = currentAccount?.pendingRequests?.length > 0

    return (
      <>
        <CustomNotificationAlert />
        {isEmpty(this.accountsWithBalance) && (
          <React.Fragment>
            <NotificationBar status="info">
              <Trans {...messages.withdrawalAccountWithoutBalance} />
              {!blockedDeposit && showDepositTab && (
                <Link to="/transactions" className={classes.link}>
                  {' '}
                  <Trans {...messages.depositNow} />{' '}
                </Link>
              )}
            </NotificationBar>
            <Grid container direction="column" justifyContent="space-between" alignItems="center">
              <Grid item className={classes.noTradesIconWidth}>
                <img
                  className={classes.emptyListsIcon}
                  src={Images[`payments-history-empty-${themePreference}.png`]}
                  alt="noTrades"
                />
              </Grid>
            </Grid>
          </React.Fragment>
        )}
        {!isEmpty(this.accountsWithBalance) && isEmpty(this.withdrawableAccountsForKyc) && (
          <Grid item xs={12}>
            <NotificationBar status={missingDocs ? 'warning' : 'error'}>
              {missingDocs ? (
                <Trans {...messages.reviewingKYCDocs} />
              ) : (
                <React.Fragment>
                  <Trans {...messages.pendingKYCDocs} />{' '}
                  <Link to="/settings/profile/documents" className={classes.link}>
                    <Trans {...messages.uploadDocuments} />
                  </Link>
                </React.Fragment>
              )}
            </NotificationBar>
          </Grid>
        )}
        {hasPendingRequests && (
          <Grid item xs={12}>
            <NotificationBar status={'info'}>
              <Trans {...messages.hasPendingRequests} />
            </NotificationBar>
          </Grid>
        )}
        {this.isPammWeekendWithdrawalBlocked && (
          <Grid item xs={12}>
            <NotificationBar status="error">
              <Trans {...messages.pammWithdrawalWeekendNotAllowed} />
            </NotificationBar>
          </Grid>
        )}
        {blockedWithdrawalsCopyTrading && this.copyTradingWithdrawalMsg(getCurrentAccount)}
        <WithdrawalBlockedPopup
          open={this.state.withdrawalBlockedPopupOpen}
          account={getCurrentAccount}
          onClose={() => this.setState({withdrawalBlockedPopupOpen: false})}
        />
        {(shouldSelectAccount || shouldSelectRefund) && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              {shouldSelectAccount ? (
                <div className={classes.chooseAccountContainer}>
                  <ChooseAccount
                    accounts={this.withdrawableAccountsForKyc}
                    disabled={hasPendingRequests}
                    submitLoading={submitLoading}
                    onContinue={this.handleSelectAccountSubmit}
                    getSelectedAccount={(accountSelected) => this.getAccountId(accountSelected)}
                    viewer={{
                      id: viewer.id,
                      locale: locale || getItem('locale', 'en'),
                      kycStatus,
                    }}
                    history={history}
                    accountId={account.id}
                    amount={
                      amount && isCentAccount(account)
                        ? amount / currencies.CUD.baseCurrencyRate!
                        : amount
                    }
                    disabledAction={
                      !!blockedWithdrawalsCopyTrading ||
                      disabledWithdrawal ||
                      this.isPammWeekendWithdrawalBlocked
                    }
                    setLydianModalData={this.setLydianModalData.bind(this)}
                    onLydianSubmit={(variables) => this.createLydianWithdrawalRequest(variables, t)}
                  />
                  {this.isShowWarningMessage ? <WarningWithdrawalSection /> : null}
                  {this.isPammInvestorAccountSelected ? <PammInvestorAccountMessage /> : null}
                </div>
              ) : null}
            </Grid>
            {blockedWithdrawalsCopyTrading && (
              <Grid item xs={12} sm={5} md={6}>
                <Grid container className={classes.makeSureDiv} spacing={3}>
                  <Grid item xs={12}>
                    <PageSubTitle>
                      <Trans {...messages.description} />{' '}
                    </PageSubTitle>
                    <ul className={classnames(classes.noteList, classes.zeroMarginTop)}>
                      <li>
                        <Typography variant="body1">
                          {' '}
                          <Trans {...messages.copyTradingWithdrawalP1} />{' '}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          {' '}
                          <Trans {...messages.copyTradingWithdrawalP2} />{' '}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="body1">
                          {' '}
                          <Trans {...messages.copyTradingWithdrawalP3} />{' '}
                        </Typography>
                        <ol className={classes.numberedList}>
                          <li>
                            <Typography variant="body1">
                              {' '}
                              <Trans {...messages.copyTradingWithdrawalP3T1} />{' '}
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body1">
                              {' '}
                              <Trans {...messages.copyTradingWithdrawalP3T2} />{' '}
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body1">
                              {' '}
                              <Trans {...messages.copyTradingWithdrawalP3T3} />{' '}
                            </Typography>
                          </li>
                        </ol>
                      </li>
                    </ul>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {/* // TODO: Cleanup flow v1 */}
            {isWithdrawalFlowV1 ? (
              shouldSelectRefund ? (
                <ChooseAvailableOption
                  account={account}
                  withdrawablePaymentMethods={withdrawablePaymentMethods}
                  amount={amount}
                  goBack={() => this.goBack()}
                  onContinue={(availableMethod) => this.refundSelected(availableMethod)}
                  viewer={{
                    locale: locale || getItem('locale', 'en'),
                    kycStatus,
                  }}
                  history={history}
                  skipRefund={() => this.setState({skipRefund: true})}
                />
              ) : null
            ) : null}
          </Grid>
        )}

        {this.isWithdrawalFlowV2 ? (
          shouldSelectMethod || shouldSelectRefund ? (
            <WithdrawalSummary
              account={account}
              clientId={viewer.id}
              bankDetails={viewer.bankDetails}
              amountToWithdraw={amountToWithdraw}
              clientType={viewer.clientType}
              clientCountry={country}
              passportNumber={viewer.passportNumber}
              withdrawablePaymentMethods={withdrawablePaymentMethods}
              redirectBack={this.goBack}
            />
          ) : null
        ) : null}

        {/* // TODO: Cleanup flow v1 */}
        {isWithdrawalFlowV1 ? (
          shouldSelectMethod ? (
            <ChooseMethod
              account={account}
              amount={amountToWithdraw}
              viewer={{
                locale: locale || getItem('locale', 'en'),
                kycStatus,
                company,
                country,
              }}
              availableMethod={!isEmpty(withdrawablePaymentMethods) ? availableMethod : {}}
              onSubmit={(variables) => this.createWithdrawalRequest(variables)}
              goBack={() => this.goBack()}
              submitLoading={submitLoading}
              onChangeMethod={() =>
                this.setState((state) => ({
                  form: {
                    ...state.form,
                    availableMethod: null,
                  },
                }))
              }
            />
          ) : null
        ) : null}

        {get(uiNotification, 'show') && (
          <UiNotification
            open={get(uiNotification, 'show')}
            status={get(uiNotification, 'status')}
            type="payment"
            buttonMessage={t(messages.close.i18nKey, messages.close.defaults)}
            onClose={(status) => {
              if (status === 'success') {
                this.checkRemainingRefunds(amountToWithdraw, availableMethod)
              } else {
                this.setState({uiNotification: {show: false, status: ''}})
              }
            }}
          >
            {methodSubmitMessage.map((message, i) => (
              <Typography variant="body1" className={classes.modalBody} key={i}>
                <Trans
                  {...message}
                  values={{
                    amount: getFormattedAmount({amount, currency: account.currency, locale}),
                    email,
                    days: get(transactionsConfig, `withdrawal.${method}.workingDays`) || '5-10',
                  }}
                  components={[
                    <strong>amount</strong>,
                    <span
                      className={
                        !get(transactionsConfig, 'withdrawal.successMessage24hours')
                          ? classes.hidden
                          : ''
                      }
                    >
                      message
                    </span>,
                    <span
                      className={
                        get(transactionsConfig, 'withdrawal.successMessage24hours')
                          ? classes.hidden
                          : ''
                      }
                    >
                      message
                    </span>,
                  ]}
                />
              </Typography>
            ))}
            {get(uiNotification, 'errorText') && (
              <Typography variant="body1" className={classes.modalBody}>
                ({get(uiNotification, 'errorText')})
              </Typography>
            )}
          </UiNotification>
        )}
        {showLydianWithdrawalNotification && (
          <UiNotification
            open={lydianWithdrawalModal.show}
            status={lydianWithdrawalModal.status || 'success'}
            type="payment"
            buttonMessage={t(messages.close.i18nKey, messages.close.defaults)}
            onClose={this.goToAccountsPage}
          >
            <Typography variant="body1">{lydianWithdrawalModal.message}</Typography>
          </UiNotification>
        )}
        {pammWithdrawalModalNotificationSuccess ? (
          <UiNotification
            open={pammWithdrawalModalNotificationSuccess.show}
            status="success"
            type="payment"
            buttonMessage={t(messages.close.i18nKey, messages.close.defaults)}
            onClose={this.goToAccountsPage}
          >
            <Typography variant="body1">
              {t(
                messages.pammWithdrawalModalNotificationSuccessTitle.i18nKey,
                messages.pammWithdrawalModalNotificationSuccessTitle.defaults,
              )}
            </Typography>
            <Typography variant="body1">
              {t(
                messages.pammWithdrawalModalNotificationSuccessBody.i18nKey,
                messages.pammWithdrawalModalNotificationSuccessBody.defaults,
              )}
            </Typography>
          </UiNotification>
        ) : null}
      </>
    )
  }
}

export default compose(
  withStyles(styles, {withTheme: true}),
  withRouter,
  withApollo,
  withNamespaces(),
  graphql(CREATE_WITHDRAWAL_MUTATION, {
    name: 'createWithdrawalRequest',
    options: {
      refetchQueries: [{query: ACCOUNTS_QUERY}],
    },
  }),
  graphql(PAYMENTS_ACCOUNTS_QUERY, {
    props: (props) => {
      const accounts: any = reject(get(props.data, 'viewer.accounts'), 'isArchived')
      return {
        error: props.data?.error,
        accountsLoading: props.data?.loading,
        accounts: map(accounts, (a) => {
          const pendingAmount =
            sumBy(
              filter(a.withdrawals, (w) => pendingWithdrawalStatuses.includes(w.status)),
              'amount',
            ) || 0
          return {
            ...a,
            pendingAmount,
          }
        }),
      }
    },
    options: {
      fetchPolicy: 'network-only',
    },
  }),
  graphql(CLIENT_DATA_QUERY, {
    props: (props) => {
      const viewer = get(props.data, 'viewer')
      return {
        error: props.data?.error,
        viewerLoading: props.data?.loading,
        viewer,
      }
    },
  }),
  graphql(PROFILE_SETTINGS_QUERY, {
    props: (props) => {
      const viewer = get(props.data, 'viewer')
      const documents = get(viewer, 'documents', [])
      return {
        error: props.data?.error,
        documentsLoading: props.data?.loading,
        documents,
      }
    },
  }),
)(Withdraw)
